import { useCallback, useRef } from "react";

// Les 8 poignées : position CSS + curseur, partagées par tous les éditeurs.
export const RESIZE_HANDLES = [
  { h: "nw", style: { top: -5, left: -5, cursor: "nwse-resize" } },
  { h: "n", style: { top: -5, left: "50%", marginLeft: -5, cursor: "ns-resize" } },
  { h: "ne", style: { top: -5, right: -5, cursor: "nesw-resize" } },
  { h: "e", style: { top: "50%", right: -5, marginTop: -5, cursor: "ew-resize" } },
  { h: "se", style: { bottom: -5, right: -5, cursor: "nwse-resize" } },
  { h: "s", style: { bottom: -5, left: "50%", marginLeft: -5, cursor: "ns-resize" } },
  { h: "sw", style: { bottom: -5, left: -5, cursor: "nesw-resize" } },
  { h: "w", style: { top: "50%", left: -5, marginTop: -5, cursor: "ew-resize" } },
];

// ---------------------------------------------------------------------------
// editorKit/useElementResize — redimensionnement générique à la souris
// (8 poignées), généralisé depuis FreeCanvas.onResizeDown/Move/Up.
//
// Fonctionne pour n'importe quel nœud sélectionné (racine ou imbriqué,
// positionné librement ou dans le flux normal) : l'appelant fournit le
// rectangle de départ (mesuré via getBoundingClientRect) et reçoit un patch
// {width, height, x, y} — x/y ne sont significatifs qu'en position libre
// (poignées nord/ouest, qui déplacent aussi l'origine).
// ---------------------------------------------------------------------------
export function useElementResize({ onResizeStart, onResize, onResizeEnd, minWidth = 20, minHeight = 20 } = {}) {
  const resize = useRef(null);

  const onPointerMove = useCallback((e) => {
    const r = resize.current;
    if (!r) return;
    const dx = e.clientX - r.startX;
    const dy = e.clientY - r.startY;
    const has = (c) => r.handle.includes(c);
    const patch = {};
    if (has("e")) patch.width = Math.max(minWidth, r.startW + dx);
    if (has("w")) {
      const w = Math.max(minWidth, r.startW - dx);
      patch.width = w;
      patch.x = r.startPosX + (r.startW - w);
    }
    if (has("s")) patch.height = Math.max(minHeight, r.startH + dy);
    if (has("n")) {
      const h = Math.max(minHeight, r.startH - dy);
      patch.height = h;
      patch.y = r.startPosY + (r.startH - h);
    }
    onResize?.(r.id, patch);
  }, [onResize, minWidth, minHeight]);

  const onPointerUp = useCallback(() => {
    const r = resize.current;
    resize.current = null;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    if (r) onResizeEnd?.(r.id);
  }, [onPointerMove, onResizeEnd]);

  // rect: {width, height} de départ (mesuré) ; pos: {x,y} de départ (mode libre uniquement)
  const startResize = useCallback((e, node, handle, rect, pos = { x: 0, y: 0 }) => {
    e.stopPropagation();
    resize.current = {
      id: node.id, handle,
      startX: e.clientX, startY: e.clientY,
      startW: rect.width, startH: rect.height,
      startPosX: pos.x || 0, startPosY: pos.y || 0,
    };
    onResizeStart?.(node.id);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  }, [onResizeStart, onPointerMove, onPointerUp]);

  return { startResize, isResizing: () => !!resize.current };
}
